/**
 * Discount Descending Sort Script for Shopify Collection Page
 * 
 * This script handles client-side sorting of products by discount percentage
 * when the "Discount: High to Low" option is selected from the SortBy dropdown.
 * 
 * FIXED: Now properly works with filter-form filters without requiring page refresh.
 * FIXED: No image flashing - uses debouncing and smart change detection.
 * FIXED: Triggers lazy image loading after sort to ensure all images load.
 */

(function() {
  'use strict';

  var SELECTORS = {
    sortSelect: '#SortBy',
    productGrid: '#CollectionProductGrid',
    productItems: '[data-product-grid-item]',
    promoBanner: '[data-promo-banner]',
    gridProductSavings: '.grid-product__price--savings',
    filterForm: '.filter-form',
    collectionAjaxContent: '#CollectionAjaxContent',
    productImages: '.grid-product__image-mask img, image-element img, .grid-product__image img'
  };

  var SORT_VALUE = 'discount-descending';
  var isProcessing = false;
  var isSorting = false;
  var debounceTimer = null;
  var lastProductCount = 0;
  var lastProductIds = '';

  /**
   * Debounce function to prevent rapid repeated calls
   */
  function debounce(func, wait) {
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        func.apply(context, args);
      }, wait);
    };
  }

  /**
   * Get a signature of current products to detect real changes
   */
  function getProductSignature() {
    var grid = document.querySelector(SELECTORS.productGrid);
    if (!grid) return '';
    var items = grid.querySelectorAll(SELECTORS.productItems);
    var ids = Array.from(items).map(function(item) {
      return item.getAttribute('data-product-id') || item.getAttribute('data-product-handle') || '';
    });
    return ids.join(',');
  }

  /**
   * Check if products have actually changed (not just DOM updates)
   */
  function hasProductsChanged() {
    var currentSignature = getProductSignature();
    var grid = document.querySelector(SELECTORS.productGrid);
    var currentCount = grid ? grid.querySelectorAll(SELECTORS.productItems).length : 0;
    
    if (currentCount !== lastProductCount || currentSignature !== lastProductIds) {
      lastProductCount = currentCount;
      lastProductIds = currentSignature;
      return true;
    }
    return false;
  }

  /**
   * Trigger lazy loading for all product images after sort
   */
  function triggerLazyImageLoad() {
    var grid = document.querySelector(SELECTORS.productGrid);
    if (!grid) return;

    var images = grid.querySelectorAll(SELECTORS.productImages);
    
    images.forEach(function(img) {
      if (img.getAttribute('loading') === 'lazy') {
        img.removeAttribute('loading');
      }
      
      if (img.dataset.src && !img.src) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset && !img.srcset) {
        img.srcset = img.dataset.srcset;
      }
      
      if (!img.complete || img.naturalHeight === 0) {
        var currentSrc = img.src || img.dataset.src;
        if (currentSrc) {
          img.src = '';
          img.src = currentSrc;
        }
      }
    });

    if (typeof theme !== 'undefined' && theme.lazyLoad) {
      try {
        theme.lazyLoad.update();
      } catch(e) {}
    }

    if (typeof AOS !== 'undefined') {
      try {
        AOS.refresh();
      } catch(e) {}
    }

    if (typeof lazySizes !== 'undefined') {
      try {
        lazySizes.autoSizer.checkElems();
      } catch(e) {}
    }

    window.dispatchEvent(new Event('scroll'));
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Initialize discount sorting functionality
   */
  function initDiscountSort() {
    var sortSelect = document.querySelector(SELECTORS.sortSelect);
    
    if (!sortSelect) {
      return;
    }

    var urlParams = new URLSearchParams(window.location.search);
    var currentSort = urlParams.get('sort_by');
    
    if (currentSort === SORT_VALUE) {
      sortSelect.value = SORT_VALUE;
      setTimeout(function() {
        applyDiscountSort();
      }, 50);
    }

    sortSelect.removeEventListener('change', handleSortChange);
    sortSelect.addEventListener('change', handleSortChange);
    
    interceptFilterFormSubmissions();
  }

  /**
   * Intercept filter form submissions to preserve discount sort
   */
  function interceptFilterFormSubmissions() {
    var filterForms = document.querySelectorAll(SELECTORS.filterForm);
    
    filterForms.forEach(function(form) {
      form.removeEventListener('input', handleFilterInput);
      form.addEventListener('input', handleFilterInput);
    });
  }

  /**
   * Handle filter input changes (debounced)
   */
  var handleFilterInput = debounce(function(event) {
    var urlParams = new URLSearchParams(window.location.search);
    var currentSort = urlParams.get('sort_by');
    
    if (currentSort === SORT_VALUE) {
      reapplyDiscountSortAfterFilter();
    }
  }, 400);

  /**
   * Reapply discount sort after filter changes
   */
  function reapplyDiscountSortAfterFilter() {
    var urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sort_by') !== SORT_VALUE) return;
    if (isSorting) return;
    
    var attempts = 0;
    var maxAttempts = 15;
    
    var checkAndSort = function() {
      attempts++;
      var grid = document.querySelector(SELECTORS.productGrid);
      var sortSelect = document.querySelector(SELECTORS.sortSelect);
      
      if (grid && grid.querySelectorAll(SELECTORS.productItems).length > 0) {
        if (sortSelect) {
          sortSelect.value = SORT_VALUE;
        }
        if (hasProductsChanged()) {
          applyDiscountSort();
        }
      } else if (attempts < maxAttempts) {
        setTimeout(checkAndSort, 150);
      }
    };
    
    checkAndSort();
  }

  /**
   * Handle sort dropdown change
   */
  function handleSortChange(event) {
    var selectedValue = event.target.value;
    
    if (selectedValue === SORT_VALUE) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      if (isProcessing) return;
      isProcessing = true;
      
      updateUrlWithSort(SORT_VALUE);
      applyDiscountSort();
      event.target.value = SORT_VALUE;
      
      setTimeout(function() {
        isProcessing = false;
      }, 200);
      
      return false;
    }
  }

  /**
   * Apply discount-based sorting to products (with flash prevention)
   */
  function applyDiscountSort() {
    if (isSorting) return;
    
    var grid = document.querySelector(SELECTORS.productGrid);
    
    if (!grid) {
      return;
    }

    var productItems = Array.from(grid.querySelectorAll(SELECTORS.productItems));
    
    if (productItems.length === 0) {
      return;
    }

    isSorting = true;
    
    var promoBanner = grid.querySelector(SELECTORS.promoBanner);
    var promoPosition = promoBanner ? parseInt(promoBanner.getAttribute('data-promo-position'), 10) : 0;
    
    if (promoBanner) {
      promoBanner.remove();
    }

    var sortedItems = productItems.slice().sort(function(a, b) {
      var discountA = parseDiscountValue(a);
      var discountB = parseDiscountValue(b);
      return discountB - discountA;
    });

    var needsReorder = false;
    for (var i = 0; i < sortedItems.length; i++) {
      if (sortedItems[i] !== productItems[i]) {
        needsReorder = true;
        break;
      }
    }

    if (needsReorder) {
      var fragment = document.createDocumentFragment();
      sortedItems.forEach(function(item) {
        fragment.appendChild(item);
      });
      grid.appendChild(fragment);
    }

    if (promoBanner && promoPosition > 0) {
      var gridItems = Array.from(grid.querySelectorAll(SELECTORS.productItems));
      
      if (gridItems.length >= promoPosition) {
        var targetProduct = gridItems[promoPosition - 1];
        if (targetProduct && targetProduct.nextSibling) {
          grid.insertBefore(promoBanner, targetProduct.nextSibling);
        } else {
          grid.appendChild(promoBanner);
        }
      } else {
        grid.appendChild(promoBanner);
      }
    }

    lastProductIds = getProductSignature();
    lastProductCount = sortedItems.length;

    setTimeout(function() {
      triggerLazyImageLoad();
    }, 50);
    
    setTimeout(function() {
      isSorting = false;
    }, 150);
    
    document.dispatchEvent(new CustomEvent('discount:sorted'));
  }

  /**
   * Parse discount percentage value from product element
   */
  function parseDiscountValue(productElement) {
    var discountAttr = productElement.getAttribute('data-discount-percent');
    if (discountAttr) {
      var discountNum = parseInt(discountAttr, 10);
      if (!isNaN(discountNum)) {
        return discountNum;
      }
    }

    var savingsEl = productElement.querySelector(SELECTORS.gridProductSavings);
    if (savingsEl) {
      var text = savingsEl.textContent || savingsEl.innerText;
      var percentMatch = text.match(/(\d+)%/);
      if (percentMatch) {
        return parseInt(percentMatch[1], 10);
      }
    }

    return 0;
  }

  /**
   * Update URL with sort parameter without reloading page
   */
  function updateUrlWithSort(sortValue) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Override the default sort change behavior for discount sorting
   */
  function overrideSortBehavior() {
    if (typeof theme !== 'undefined' && theme.Collection) {
      var originalOnSortChange = theme.Collection.prototype.onSortChange;
      
      theme.Collection.prototype.onSortChange = function() {
        var sortValue = this.getSortValue();
        
        if (sortValue === SORT_VALUE) {
          updateUrlWithSort(SORT_VALUE);
          applyDiscountSort();
          return;
        }
        
        if (originalOnSortChange) {
          originalOnSortChange.call(this);
        }
      };
      
      var originalRenderCollectionPage = theme.Collection.prototype.renderCollectionPage;
      if (originalRenderCollectionPage) {
        theme.Collection.prototype.renderCollectionPage = function(searchParams) {
          var urlParams = new URLSearchParams(window.location.search);
          var currentSort = urlParams.get('sort_by');
          
          if (currentSort === SORT_VALUE) {
            searchParams.set('sort_by', SORT_VALUE);
          }
          
          originalRenderCollectionPage.call(this, searchParams);
        };
      }
    }
  }

  /**
   * Set up MutationObserver with debouncing to detect DOM changes
   */
  function setupMutationObserver() {
    var ajaxContent = document.querySelector(SELECTORS.collectionAjaxContent);
    if (!ajaxContent) return;

    var observerDebounceTimer = null;
    
    var observer = new MutationObserver(function(mutations) {
      var urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sort_by') !== SORT_VALUE) return;
      if (isSorting) return;
      
      var hasSignificantChange = mutations.some(function(mutation) {
        if (mutation.type === 'childList') {
          var addedNodes = Array.from(mutation.addedNodes);
          return addedNodes.some(function(node) {
            return node.nodeType === 1 && (
              node.matches && (
                node.matches(SELECTORS.productItems) ||
                node.matches(SELECTORS.productGrid) ||
                node.querySelector && node.querySelector(SELECTORS.productItems)
              )
            );
          });
        }
        return false;
      });
      
      if (!hasSignificantChange) return;
      
      clearTimeout(observerDebounceTimer);
      observerDebounceTimer = setTimeout(function() {
        if (hasProductsChanged()) {
          var sortSelect = document.querySelector(SELECTORS.sortSelect);
          if (sortSelect) {
            sortSelect.value = SORT_VALUE;
          }
          applyDiscountSort();
          interceptFilterFormSubmissions();
        }
      }, 200);
    });

    observer.observe(ajaxContent, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initDiscountSort();
      overrideSortBehavior();
      setupMutationObserver();
    });
  } else {
    initDiscountSort();
    overrideSortBehavior();
    setupMutationObserver();
  }

  var debouncedAjaxHandler = debounce(function() {
    var urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sort_by') === SORT_VALUE && hasProductsChanged()) {
      var sortSelect = document.querySelector(SELECTORS.sortSelect);
      if (sortSelect) {
        sortSelect.value = SORT_VALUE;
      }
      applyDiscountSort();
      interceptFilterFormSubmissions();
    }
  }, 200);

  document.addEventListener('ajaxify:updated', debouncedAjaxHandler);

  if (typeof theme !== 'undefined') {
    document.addEventListener('theme:collection:change', debounce(function() {
      var urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sort_by') === SORT_VALUE && hasProductsChanged()) {
        var sortSelect = document.querySelector(SELECTORS.sortSelect);
        if (sortSelect) {
          sortSelect.value = SORT_VALUE;
        }
        applyDiscountSort();
        interceptFilterFormSubmissions();
      }
    }, 200));
  }

  window.addEventListener('popstate', function() {
    var urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sort_by') === SORT_VALUE) {
      setTimeout(function() {
        lastProductIds = '';
        lastProductCount = 0;
        initDiscountSort();
      }, 100);
    }
  });

})();
